import { useEffect, useRef, useState } from 'react'
import { useDispatch } from 'react-redux'
import { getUserApi } from '@/api/common'
import { UserRes } from '@/types/api'
import useToken from '@/hooks/useToken'
import useAfterInformation from '@/hooks/useAfterOnboarding'
import { isSSR } from '@/utils/is'
import { backendCodeProcessor } from '@/api/request'
import dayjs from 'dayjs'
import { Language } from '@/utils/language'
import { useRouter } from 'next/router'
import useLocalUserInfo from '@/hooks/useLocalUserInfo'
import { MULTIPLE_LOGIN_CODE } from '@/utils/constants'
import { deleteToken } from '@/utils/checkLogin'

const useIntercom = ({ lang }: { lang: Language }) => {
  const [isIntercomLoad, setIsIntercomLoad] = useState(false)
  const [pollTimes, setPollTimes] = useState(0)
  const loadedRef = useRef<NodeJS.Timer>()

  const router = useRouter()
  const dispatch = useDispatch()
  const { afterRegisterSuccess } = useAfterInformation({ rememberPassword: false })
  const { isLogin } = useToken()
  const { addLocalUserInfo, deleteLocalUserInfo } = useLocalUserInfo()

  const checkLoaded = () => {
    if (window?.Intercom) {
      setIsIntercomLoad(true)
      loadedRef.current && clearInterval(loadedRef.current)
      return
    } else if (pollTimes >= 6) {
      loadedRef.current && clearInterval(loadedRef.current)
      return
    } else {
      setPollTimes(pollTimes + 1)
    }
  }

  async function fetchUserInfo() {
    return getUserApi()
      .then(backendCodeProcessor)
      .then((data) => {
        dispatch({
          type: 'update-userInfo',
          payload: { userInfo: data }
        })

        return data
      })
      .catch((e) => {
        if (e.data?.code === MULTIPLE_LOGIN_CODE) {
          deleteLocalUserInfo()
          deleteToken()
        }
      })
  }

  function addGoogleTag(userId: number) {
    if (process.env.NODE_ENV === 'development') {
      return
    }

    try {
      window?.gtag('config', 'G-F9R54EGLQV', { user_id: userId })
      window?.gtag('config', 'UA-233959850-1', { user_id: userId })
    } catch (error) {
      console.log(error)
    }
  }

  function addIntercomParams(userInfo?: UserRes) {
    if (isSSR || process.env.NODE_ENV === 'development') {
      return
    }

    try {
      if (userInfo && userInfo?.intercom?.user_hash) {
        window.intercomSettings = {
          api_base: 'https://api-iam.intercom.io',
          app_id: 'jr0fp2wt',
          name: userInfo.name,
          email: userInfo.email,
          created_at: dayjs(userInfo.create_time).unix(),
          user_id: userInfo?.user_id,
          user_hash: userInfo?.intercom?.user_hash,
          // intercom上配置的就是首字母大写的
          Role: userInfo?.role?.name,
          LanguageBySite: lang,
          echotik_member: userInfo?.member?.plan?.id,
          echotik_pro_expire: userInfo?.member?.expire_time,
          echotik_signup_completion_time: userInfo?.email_verified_at
        }
      } else {
        window.intercomSettings = {
          api_base: 'https://api-iam.intercom.io',
          app_id: 'jr0fp2wt',
          LanguageBySite: lang
        }
      }
    } catch (error) {
      console.log(error)
    }
  }

  useEffect(() => {
    loadedRef.current = setInterval(checkLoaded, 5000)

    return () => {
      loadedRef.current && clearInterval(loadedRef.current)
    }
  }, [])

  useEffect(() => {
    if (isLogin) {
      fetchUserInfo().then((userInfo) => {
        if (!userInfo?.role) {
          afterRegisterSuccess(userInfo, {})
        }

        addLocalUserInfo(userInfo)

        addIntercomParams(userInfo)
        addGoogleTag(userInfo?.user_id)
      })
    } else {
      addIntercomParams()
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return {
    isIntercomLoad
  }
}

export default useIntercom
